import { Button, Col, Flex, Form, Input, Layout, Row, Tooltip } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import CustomSelectAll from 'src/app/shared/components/common/select-all'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useDatePicker } from 'src/app/shared/hooks/useDatePicker'
import { useDisableFutureDates } from 'src/app/shared/hooks/useDisableFutureDates'
import { getAuditTrailLogActions, getAuditTrailLogTableNames, getAuditTrailLogUsers } from 'src/app/shared/services/BackgroundService'
import { ArrowRightIcon, CalendarCustomIcon, RefreshIcon, WhiteSearchIcon } from 'src/assets/icons'
import { useAuditTrailLog } from './useAuditTrailLog'
import { FilterOptionEnum } from 'src/app/shared/models/auditTrailLog.model'
import ActionTag from './ActionTag'
import './index.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

interface AuditTrailLogFormValues {
    date: [dayjs.Dayjs, dayjs.Dayjs] | null
    clientName: string
    accountNumber: string
    userIds: (string | null)[]
    tableNames: (string | null)[]
    actions: (string | null)[]
}

const AuditTrailLog: React.FC = () => {
    const { t } = useTranslation()
    const { getCurrentDate, timeZoneId } = useDatePicker()
    const { disableFutureDates } = useDisableFutureDates()
    const { resultList, isGettingList, getResultList } = useAuditTrailLog()

    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false)
    const [isLoadingTables, setIsLoadingTables] = useState<boolean>(false)
    const [isLoadingActions, setIsLoadingActions] = useState<boolean>(false)

    const [userList, setUserList] = useState<{ userId: string; userName: string }[]>([])
    const [tableList, setTableList] = useState<string[]>([])
    const [actionList, setActionList] = useState<string[]>([])

    const [currentFilters, setCurrentFilters] = useState<Partial<AuditTrailLogFormValues>>({})
    const currentFiltersRef = useRef<Partial<AuditTrailLogFormValues>>({})
    const tableComponentRef = useRef<any>(null)

    const defaultValues: AuditTrailLogFormValues = {
        date: [getCurrentDate().subtract(1, 'day'), getCurrentDate()],
        clientName: '',
        accountNumber: '',
        userIds: [null as any],
        tableNames: [null as any],
        actions: [null as any],
    }

    const { control, handleSubmit, setValue, reset, setError, clearErrors, trigger, watch, getValues } = useForm<AuditTrailLogFormValues>({
        mode: 'all',
        defaultValues,
    })

    const watchedDate = watch('date')
    const watchedTableNames = watch('tableNames')
    const watchedUserIds = watch('userIds')
    const watchedActions = watch('actions')

    const formatDateString = useCallback(
        (dateString: any) => dayjs(dateString).tz(timeZoneId).format('YYYY-MM-DD'),
        [timeZoneId]
    )

    const fetchUsers = useCallback(async () => {
        const dateFrom = watchedDate?.[0] || getCurrentDate().subtract(1, 'day')
        const dateTo = watchedDate?.[1] || getCurrentDate()

        try {
            setIsLoadingUsers(true)
            const payload: Partial<any> = {
                dateFrom: formatDateString(dateFrom),
                dateTo: formatDateString(dateTo),
            }

            if (watchedTableNames && !watchedTableNames.includes(null)) {
                payload.tableNames = watchedTableNames.filter((name): name is string => name !== null)
            }

            const response = await getAuditTrailLogUsers(payload)
            if (response?.data) {
                setUserList(response.data)
            }
        } catch (error) {
            console.log('ERROR: ', error)
            setUserList([])
        } finally {
            setIsLoadingUsers(false)
        }
    }, [watchedDate, watchedTableNames, formatDateString, getCurrentDate])

    const fetchTables = useCallback(async () => {
        try {
            setIsLoadingTables(true)
            const response = await getAuditTrailLogTableNames()
            if (response?.data) {
                setTableList(response.data)
            }
        } catch (error) {
            console.log('ERROR: ', error)
            setTableList([])
        } finally {
            setIsLoadingTables(false)
        }
    }, [])

    const fetchActions = useCallback(async () => {
        try {
            setIsLoadingActions(true)
            const response = await getAuditTrailLogActions()
            if (response?.data) {
                setActionList(response.data)
            }
        } catch (error) {
            console.log('ERROR: ', error)
            setActionList([])
        } finally {
            setIsLoadingActions(false)
        }
    }, [])

    const onSubmit = useCallback(
        async (values: AuditTrailLogFormValues) => {
            currentFiltersRef.current = values
            setCurrentFilters(values)
            await tableComponentRef.current?.handleSearchLogic?.({ pageIndex: 1 })
        },
        [],
    )

    const isInitializedRef = useRef(false)

    useEffect(() => {
        const initialize = async () => {
            await fetchUsers()
            await fetchActions()
            await fetchTables()
            const values = getValues()
            currentFiltersRef.current = values
            setCurrentFilters(values)
            isInitializedRef.current = true
        }
        initialize()
    }, [])

    useEffect(() => {
        if (!isInitializedRef.current) return
        fetchUsers()
    }, [watchedDate, watchedTableNames])

    useEffect(() => {
        if (!isInitializedRef.current) return
        if (!watchedDate?.[0] || !watchedDate?.[1]) return
        const values = getValues()
        currentFiltersRef.current = values
        setCurrentFilters(values)
        tableComponentRef.current?.handleSearchLogic?.({ pageIndex: 1 })
    }, [watchedDate, watchedUserIds, watchedTableNames, watchedActions])

    const handleReset = () => {
        reset(defaultValues)
    }

    const handleSearch = useCallback(
        async (value: any) => {
            const filters = currentFiltersRef.current
            const payload = {
                ...value,
                dateFrom: filters?.date?.[0] ? formatDateString(filters?.date?.[0]) : '',
                dateTo: filters?.date?.[1] ? formatDateString(filters?.date?.[1]) : '',
                clientName: filters?.clientName?.trim() || undefined,
                accountNumber: filters?.accountNumber?.trim() || undefined,
                userIds: filters?.userIds?.includes(null)
                    ? undefined
                    : (filters?.userIds?.filter((id): id is string => id !== null) as string[]),
                tableNames: filters?.tableNames?.includes(null)
                    ? undefined
                    : (filters?.tableNames?.filter((name): name is string => name !== null) as string[]),
                actions: filters?.actions?.includes(null)
                    ? undefined
                    : (filters?.actions?.filter((action): action is string => action !== null) as string[]),
                filterOption: FilterOptionEnum.List,
            }
            await getResultList(payload)
        },
        [getResultList, formatDateString],
    )

    const handleDownload = useCallback(
        async (payload: any) => {
            const filters = currentFiltersRef.current
            const downloadPayload = {
                ...payload,
                dateFrom: filters?.date?.[0] ? formatDateString(filters?.date?.[0]) : '',
                dateTo: filters?.date?.[1] ? formatDateString(filters?.date?.[1]) : '',
                clientName: filters?.clientName?.trim() || undefined,
                accountNumber: filters?.accountNumber?.trim() || undefined,
                userIds: filters?.userIds?.includes(null)
                    ? undefined
                    : (filters?.userIds?.filter((id): id is string => id !== null) as string[]),
                tableNames: filters?.tableNames?.includes(null)
                    ? undefined
                    : (filters?.tableNames?.filter((name): name is string => name !== null) as string[]),
                actions: filters?.actions?.includes(null)
                    ? undefined
                    : (filters?.actions?.filter((action): action is string => action !== null) as string[]),
            }
            return await getResultList(downloadPayload, true)
        },
        [getResultList, formatDateString],
    )

    const formatFieldName = (fieldName: string): string => {
        return fieldName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim()
    }

    const renderFormattedJson = (value: string | null) => {
        if (!value) return '-'

        try {
            const parsed = JSON.parse(value)
            return (
                <>
                    {Object.entries(parsed).map(([key, val], index) => (
                        <span key={key}>
                            {formatFieldName(key)}: {val === null ? 'null' : String(val)}
                            {index < Object.entries(parsed).length - 1 && <br />}
                        </span>
                    ))}
                </>
            )
        } catch {
            return value
        }
    }

    const renderChangedFields = (changedValues: string | null) => {
        if (!changedValues) return '-'

        try {
            const parsed = JSON.parse(changedValues)
            const fieldNames = Object.keys(parsed)

            if (fieldNames.length === 0) return '-'

            return (
                <>
                    {fieldNames.map((field, index) => (
                        <span key={field}>
                            {formatFieldName(field)}
                            {index < fieldNames.length - 1 && <br />}
                        </span>
                    ))}
                </>
            )
        } catch {
            return '-'
        }
    }

    const generateCustomColumnDefs = useCallback(
        (columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
            columnDefs.map((item) => {
                const { colId } = item

                if (colId === 'date') {
                    return {
                        ...item,
                        field: 'createdAt',
                    }
                }

                if (colId === 'user') {
                    return {
                        ...item,
                        field: 'userName',
                    }
                }

                if (colId === 'table') {
                    return {
                        ...item,
                        field: 'tableName',
                    }
                }

                if (colId === 'key') {
                    return {
                        ...item,
                        field: 'keyValues',
                        cellRenderer: (props: any) => {
                            const { data } = props
                            return renderFormattedJson(data?.keyValues)
                        },
                    }
                }

                if (colId === 'action') {
                    return {
                        ...item,
                        cellRenderer: (props: any) => {
                            const { data } = props
                            return <ActionTag action={data?.action} />
                        },
                    }
                }

                if (colId === 'oldValues' || colId === 'newValues' || colId === 'keyValues') {
                    return {
                        ...item,
                        cellRenderer: (props: any) => {
                            const { data } = props
                            const value = data[colId]
                            return renderFormattedJson(value)
                        },
                    }
                }

                if (colId === 'changes') {
                    return {
                        ...item,
                        cellRenderer: (props: any) => {
                            const { data } = props
                            return renderChangedFields(data?.changedValues)
                        },
                    }
                }

                return item
            }),
        [],
    )

    return (
        <Layout.Content style={{ padding: 24 }} className='audit-trail-log-container'>
            <Form onFinish={handleSubmit(onSubmit)} layout='vertical'>
                <Flex style={{ flexDirection: 'column', gap: 24 }}>
                    <Row gutter={24}>
                        <Col span={24}>
                            <Controller
                                control={control}
                                name='date'
                                render={({ field, fieldState }) => (
                                    <CustomRangePicker
                                        label={t('AuditTrailLog:auditTrailLog.field.date')}
                                        value={field.value}
                                        onChange={async (e: any) => {
                                            field.onChange(e)
                                            if (!e) {
                                                setError('date', {
                                                    type: 'required',
                                                    message: t('AuditTrailLog:auditTrailLog.validation.dateRequired'),
                                                })
                                            } else {
                                                clearErrors('date')
                                            }
                                        }}
                                        onBlur={async () => {
                                            field.onBlur()
                                            await trigger('date')
                                        }}
                                        error={fieldState.error}
                                        placeholder={[
                                            t('AuditTrailLog:auditTrailLog.placeholder.selectDateFrom'),
                                            t('AuditTrailLog:auditTrailLog.placeholder.selectDateTo'),
                                        ]}
                                        separator={<ArrowRightIcon className='audit-trail-log-separator' />}
                                        suffixIcon={<CalendarCustomIcon />}
                                        disabledDate={disableFutureDates}
                                        inputReadOnly
                                        required
                                    />
                                )}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('AuditTrailLog:auditTrailLog.validation.dateRequired'),
                                    },
                                }}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Controller
                                control={control}
                                name='clientName'
                                render={({ field }) => (
                                    <Form.Item label={t('AuditTrailLog:auditTrailLog.field.clientName')} className='bold-label'>
                                        <Input
                                            {...field}
                                            placeholder={t('AuditTrailLog:auditTrailLog.placeholder.enterClientName')}
                                            maxLength={100}
                                        />
                                    </Form.Item>
                                )}
                            />
                        </Col>
                        <Col span={12}>
                            <Controller
                                control={control}
                                name='accountNumber'
                                render={({ field }) => (
                                    <Form.Item label={t('AuditTrailLog:auditTrailLog.field.accountNumber')} className='bold-label'>
                                        <Input
                                            {...field}
                                            placeholder={t('AuditTrailLog:auditTrailLog.placeholder.enterAccountNumber')}
                                            maxLength={50}
                                        />
                                    </Form.Item>
                                )}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={8}>
                            <Controller
                                control={control}
                                name='userIds'
                                render={({ field, fieldState }) => (
                                    <CustomSelectAll
                                        label={t('AuditTrailLog:auditTrailLog.field.user')}
                                        placeholder={t('AuditTrailLog:auditTrailLog.placeholder.selectUsers')}
                                        value={field.value}
                                        onChange={(data: any) => {
                                            if (!data || data?.length === 0) {
                                                setValue('userIds', [null as any])
                                            } else if (data?.includes(null)) {
                                                if (!data[0]) {
                                                    const newData = data?.slice(1)
                                                    setValue('userIds', [...newData])
                                                } else {
                                                    setValue('userIds', [null as any])
                                                }
                                            } else {
                                                setValue('userIds', data)
                                            }
                                        }}
                                        onBlur={field.onBlur}
                                        error={fieldState.error}
                                        options={
                                            userList?.map((item) => ({
                                                label: item.userName,
                                                value: item.userId,
                                            })) ?? []
                                        }
                                        mode='multiple'
                                        defaultActiveFirstOption
                                        allowClear={!field.value?.includes(null)}
                                        loading={isLoadingUsers}
                                        defaultOption={{
                                            label: 'All Users',
                                            value: null,
                                        }}
                                        maxTagCount={'responsive'}
                                        maxTagPlaceholder={(omittedValues) => (
                                            <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                                                <span>+ {omittedValues.length}...</span>
                                            </Tooltip>
                                        )}
                                    />
                                )}
                            />
                        </Col>
                        <Col span={8}>
                            <Controller
                                control={control}
                                name='tableNames'
                                render={({ field, fieldState }) => (
                                    <CustomSelectAll
                                        label={t('AuditTrailLog:auditTrailLog.field.table')}
                                        placeholder={t('AuditTrailLog:auditTrailLog.placeholder.selectTables')}
                                        value={field.value}
                                        onChange={(data: any) => {
                                            if (!data || data?.length === 0) {
                                                setValue('tableNames', [null as any])
                                            } else if (data?.includes(null)) {
                                                if (!data[0]) {
                                                    const newData = data?.slice(1)
                                                    setValue('tableNames', [...newData])
                                                } else {
                                                    setValue('tableNames', [null as any])
                                                }
                                            } else {
                                                setValue('tableNames', data)
                                            }
                                        }}
                                        onBlur={field.onBlur}
                                        error={fieldState.error}
                                        options={
                                            tableList?.map((item) => ({
                                                label: item,
                                                value: item,
                                            })) ?? []
                                        }
                                        mode='multiple'
                                        defaultActiveFirstOption
                                        allowClear={!field.value?.includes(null)}
                                        loading={isLoadingTables}
                                        defaultOption={{
                                            label: 'All Tables',
                                            value: null,
                                        }}
                                        maxTagCount={'responsive'}
                                        maxTagPlaceholder={(omittedValues) => (
                                            <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                                                <span>+ {omittedValues.length}...</span>
                                            </Tooltip>
                                        )}
                                    />
                                )}
                            />
                        </Col>
                        <Col span={8}>
                            <Controller
                                control={control}
                                name='actions'
                                render={({ field, fieldState }) => (
                                    <CustomSelectAll
                                        label={t('AuditTrailLog:auditTrailLog.field.action')}
                                        placeholder={t('AuditTrailLog:auditTrailLog.placeholder.selectActions')}
                                        value={field.value}
                                        onChange={(data: any) => {
                                            if (!data || data?.length === 0) {
                                                setValue('actions', [null as any])
                                            } else if (data?.includes(null)) {
                                                if (!data[0]) {
                                                    const newData = data?.slice(1)
                                                    setValue('actions', [...newData])
                                                } else {
                                                    setValue('actions', [null as any])
                                                }
                                            } else {
                                                setValue('actions', data)
                                            }
                                        }}
                                        onBlur={field.onBlur}
                                        error={fieldState.error}
                                        options={
                                            actionList?.map((item) => ({
                                                label: item,
                                                value: item,
                                            })) ?? []
                                        }
                                        mode='multiple'
                                        defaultActiveFirstOption
                                        allowClear={!field.value?.includes(null)}
                                        loading={isLoadingActions}
                                        defaultOption={{
                                            label: 'All Actions',
                                            value: null,
                                        }}
                                        maxTagCount={'responsive'}
                                        maxTagPlaceholder={(omittedValues) => (
                                            <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                                                <span>+ {omittedValues.length}...</span>
                                            </Tooltip>
                                        )}
                                    />
                                )}
                            />
                        </Col>
                    </Row>

                    <Flex style={{ justifyContent: 'flex-end', gap: 12 }} className='check-button-group'>
                        <Button className='reset-btn' onClick={handleReset} icon={<RefreshIcon />}>
                            {t('AuditTrailLog:auditTrailLog.button.reset')}
                        </Button>
                        <Button
                            type='primary'
                            className='search-check-btn'
                            htmlType='submit'
                            loading={isGettingList}
                            icon={<WhiteSearchIcon />}
                        >
                            {t('AuditTrailLog:auditTrailLog.button.search')}
                        </Button>
                    </Flex>
                </Flex>
            </Form>

            {(resultList?.items?.length > 0 || Object.keys(currentFilters).length > 0) && (
                <div style={{ marginTop: 24 }}>
                    <NewCommonTable
                        ref={tableComponentRef}
                        rowData={resultList?.items}
                        total={resultList?.total}
                        displayAddBtn={false}
                        translationNamespace='AuditTrailLog:auditTrailLog.column'
                        displayUploadButton={false}
                        displayActionColumn={false}
                        displaySearch={false}
                        displayDownloadBtn={false}
                        displayColumnBtn={false}
                        displayResetButton={false}
                        isGettingList={isGettingList}
                        loading={isGettingList}
                        pageName={TABLE_NAME.AUDIT_TRAIL_LOG}
                        handleSearch={handleSearch}
                        handleDownload={handleDownload}
                        tableSettingName={TABLE_NAME.AUDIT_TRAIL_LOG}
                        generateCustomColumnDefs={generateCustomColumnDefs}
                    />
                </div>
            )}
        </Layout.Content>
    )
}

export default AuditTrailLog